import { CrewButton } from 'components/elements/crewButton/crewButton'
import { CrewTextBox } from 'components/devextreme/crewTextBox'
import { FC, memo } from 'react'
import { SEARCH_TIMEOUT_MSEC } from '@crew/configs/constants'
import { useCallback, useMemo, useState, useRef } from 'react'
import { useTranslation } from '@crew/modules/i18n'
import { debounce } from 'lodash'
import { useModal } from 'components/layouts/modal/useModal'
import { TextBox } from 'devextreme-react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { useProjectPermissions } from '@crew/hooks'
import { EntityType } from '@crew/enums/domain'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useToast } from 'hooks/useToast'
import { CrewConfirmDialog } from 'components/elements/crewConfirmDialog/crewConfirmDialog'
import { useGroupChatMemberListContext } from '../../useGroupChatMemberList'
import { GroupChatAddMemberDialog } from 'features/groupChat/components/groupChatAddMemberDialog/groupChatAddMemberDialog'
import { useGroupChatMemberListToolbar } from './useGroupChatMemberListToolbar'
import qs from 'qs'

export const GroupChatMemberListToolbar: FC = memo(() => {
  const { t } = useTranslation()
  const { success, error } = useToast()
  const location = useLocation()
  const { navigate } = useCrewNavigate()
  const [searchParams] = useSearchParams()
  const { projectId } = useParams()
  const [showApiErrors] = useShowApiErrors()

  const [keyword, setKeyword] = useState(searchParams.get('keyword') ?? '')

  const { hasPrjMemberDeletePermission, hasPrjMemberAddPermission } =
    useProjectPermissions(EntityType.Project, projectId)

  const {
    insertGroupMembers,
    isLoadingInsertGroupMember,
    deleteGroupMembers,
    isLoadingDeleteGroupMembers,
  } = useGroupChatMemberListToolbar()

  // メンバー追加ダイアログ用
  const [
    isGroupChatAddMemberDialogOpen,
    openGroupChatAddMemberDialog,
    closeGroupChatAddMemberDialog,
  ] = useModal()

  // 複数メンバー削除確認ダイアログ
  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
    useModal()
  const [confirmMessage, setConfirmMessage] = useState('')

  // 選択したメンバーのユーザーIDのリスト
  const { selectedUserIds, setSelectedUserIds } =
    useGroupChatMemberListContext()

  // 選択したメンバーがいない場合、クエリ実行中は、削除ボタンを無効にする
  const isDisable = useMemo(
    () =>
      selectedUserIds.length === 0 ||
      isLoadingInsertGroupMember ||
      isLoadingDeleteGroupMembers,
    [
      isLoadingDeleteGroupMembers,
      isLoadingInsertGroupMember,
      selectedUserIds.length,
    ]
  )

  // キーワード検索のkeyupイベント後に500ms遅延させて検索する
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        const currentParams: any = qs.parse(searchParams.toString())
        const newParams = {
          ...currentParams,
          keyword: value || undefined,
        }
        navigate(location.pathname, newParams, true)
      }, SEARCH_TIMEOUT_MSEC),
    [location.pathname, navigate, searchParams]
  )

  const keywordTextBoxRef = useRef<TextBox>(null)
  // キーワード検索ボックスのkeyupイベント時に関数を呼び出す
  const handleKeywordTextBoxInput = useCallback(() => {
    // onInputイベントでは入力値をvalueで取得できないので、instanceから直接取得する
    const input = keywordTextBoxRef.current?.instance.option('text')
    if (input !== undefined && input !== keyword) {
      setKeyword(input)
      debouncedSearch(input)
    }
  }, [debouncedSearch, keyword])

  // 複数メンバー削除ボタンクリック時の処理
  const handleDeleteMultipleButtonClick = useCallback(() => {
    setConfirmMessage(t('message.general.confirmMessage.delete'))
    openConfirmDialog()
  }, [openConfirmDialog, t])

  // 削除確認ダイアログ OKボタン
  const handleDeletePermitButtonClick = useCallback(async () => {
    if (projectId) {
      try {
        // プロジェクトメンバー削除処理を実行
        await deleteGroupMembers(projectId, selectedUserIds)

        success(t('message.groupChat.groupChatMemberDeleted'))

        // 選択行の初期化
        setSelectedUserIds([])
      } catch (err) {
        error(t('message.general.errorMessage.delete'))
      }
    }
    closeConfirmDialog()
  }, [
    closeConfirmDialog,
    deleteGroupMembers,
    error,
    projectId,
    selectedUserIds,
    setSelectedUserIds,
    success,
    t,
  ])

  // メンバー追加処理
  const handleAddUsers = useCallback(
    async (userIds: string[]) => {
      if (!projectId || userIds.length === 0) return
      try {
        // プロジェクトメンバー追加処理
        await insertGroupMembers(projectId, userIds)

        success(t('message.groupChat.groupChatMemberRegistered'))
      } catch (error) {
        showApiErrors(error)
      }
    },
    [insertGroupMembers, projectId, showApiErrors, success, t]
  )

  return (
    <div className="flex justify-between items-start p-2 gap-x-2.5">
      <div className="flex gap-x-2.5">
        {/* キーワード */}
        <CrewTextBox
          className="w-40"
          value={keyword}
          valueChangeEvent="input change"
          onInput={handleKeywordTextBoxInput}
          showClearButton={true}
          ref={keywordTextBoxRef}
          mode="search"
        />
      </div>
      <div className="flex gap-2.5 overflow-x-auto">
        {/* メンバーを追加 */}
        {hasPrjMemberAddPermission && (
          <CrewButton
            id="group-chat-member-list-toolbar-add-member-button"
            type="primary"
            text={t('action.addMember')}
            onClick={openGroupChatAddMemberDialog}
          />
        )}
        {/* メンバーを削除 */}
        {hasPrjMemberDeletePermission && (
          <CrewButton
            id="group-chat-member-list-toolbar-remove-member-button"
            type="primary"
            text={t('action.removeMember')}
            disabled={isDisable}
            onClick={handleDeleteMultipleButtonClick}
          />
        )}
      </div>

      {/* メンバー追加ダイアログ */}
      {projectId && (
        <GroupChatAddMemberDialog
          projectId={projectId}
          isOpen={isGroupChatAddMemberDialogOpen}
          onAddUsers={handleAddUsers}
          onClose={closeGroupChatAddMemberDialog}
        />
      )}

      {/* 確認ダイアログ */}
      <CrewConfirmDialog
        isOpen={isConfirmDialogOpen}
        message={confirmMessage}
        onPermitButtonClick={handleDeletePermitButtonClick}
        onCancelButtonClick={closeConfirmDialog}
        permitButtonDisabled={isLoadingDeleteGroupMembers}
      />
    </div>
  )
})
